import type { Action } from "@/components/MenuButton";
import type { Person, PhoneNumber } from "@/types";
import { capitalize } from "lodash";

import { buildMailtoHref, buildSmsHref, buildTelHref, getMobileAndHomePhones } from "./utils";

function phoneAction(phone: PhoneNumber): Action | null {
  const href = buildTelHref(phone);
  if (!href) return null;

  const phoneTypeLabel = capitalize(phone.phoneType);

  return {
    text: `Call ${phoneTypeLabel}`,
    supportingText: phone.phoneFormatted ?? undefined,
    supportingTextWrap: false,
    iconName: "call",
    ...(phone.phoneFormatted
      ? { copyable: true, copyText: phone.phoneFormatted, copyFieldLabel: `${phoneTypeLabel} Phone` }
      : {}),
    onClick: () => {
      window.location.href = href;
    },
    testId: `contact-call-${phone.phoneType}`,
    menuOnly: true
  };
}

function textAction(phone: PhoneNumber): Action | null {
  const href = buildSmsHref(phone);
  if (!href) return null;

  const phoneTypeLabel = capitalize(phone.phoneType);

  return {
    text: `Text ${phoneTypeLabel}`,
    supportingText: phone.phoneFormatted ?? undefined,
    supportingTextWrap: false,
    iconName: "sms",
    ...(phone.phoneFormatted
      ? { copyable: true, copyText: phone.phoneFormatted, copyFieldLabel: `${phoneTypeLabel} Phone` }
      : {}),
    onClick: () => {
      window.location.href = href;
    },
    testId: `contact-text-${phone.phoneType}`,
    menuOnly: true
  };
}

export function buildPersonContactMenuItems(person: Pick<Person, "phoneNumbers" | "email">): Action[] {
  const items: Action[] = [];
  const { mobile: mobilePhone, home: homePhone } = getMobileAndHomePhones(person.phoneNumbers);

  const pushCallAndText = (phone: PhoneNumber) => {
    const callItem = phoneAction(phone);
    if (callItem) items.push(callItem);

    const smsItem = textAction(phone);
    if (smsItem) items.push(smsItem);
  };

  if (mobilePhone) pushCallAndText(mobilePhone);
  if (homePhone) pushCallAndText(homePhone);

  const trimmedEmail = person.email?.trim();
  const mailtoHref = buildMailtoHref(trimmedEmail);
  if (mailtoHref) {
    items.push({
      text: "Email",
      supportingText: trimmedEmail,
      iconName: "mail",
      copyable: true,
      copyText: trimmedEmail,
      copyFieldLabel: "Email",
      onClick: () => {
        window.location.href = mailtoHref;
      },
      testId: "contact-email",
      menuOnly: true
    });
  }

  return items;
}
